import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowSquareOut, ArrowsClockwise, TrashSimple } from '@phosphor-icons/react';
import { Alert, AlertDescription, AlertHeader, AlertInlineButton } from '@clay-run/glaze/alert';
import { Badge } from '@clay-run/glaze/badge';
import { Button } from '@clay-run/glaze/button';
import { Heading } from '@clay-run/glaze/heading';
import { Icon } from '@clay-run/glaze/icon';
import { InputField } from '@clay-run/glaze/input-field';
import { LoadingSpinner } from '@clay-run/glaze/loading-spinner';
import { StatusBadge } from '@clay-run/glaze/status-badge';
import { Text } from '@clay-run/glaze/text';
import { addErrorToast, addToast } from '@clay-run/glaze/toast/toast-queue';

import { useAppBasePath } from '../../_shared/base-path';
import { useApi, useCurrentUser } from '../../_shared/hooks';
import { formatEventDate } from '../dates';
import { playSendsQueryKey } from '../dispatch-inspector-logic';
import { isLauncherAdmin } from '../config-page-logic';
import { getPlaybooksSettings, updatePlaybooksSettings } from '../api-client';
import { resetPlayToDraft, wipePlayDispatchLog } from '../mutations';
import { settingsQueryKey } from '../query-keys';
import {
  mergePlaybookNames,
  playDisplayName,
  playStatusBadgeStatus,
  playStatusLabel,
} from '../plays-logic';
import {
  describeTransport,
  hasSettingsChange,
  launchedPlayRows,
  normalizeRoutineIdInput,
  resetPlayWarning,
  rowActionErrorMessage,
  wipeLogWarning,
} from '../settings-page-logic';
import { usePlaysLibrary, invalidatePlayLists } from '../use-plays-library';
import { ConfirmActionDialog } from './ConfirmActionDialog';
import { EmptySectionRow } from './PlaybooksConfigPage';
import { useGuardedFlowEntry } from './UnsavedWorkGuard';

const SETTINGS_QUERY_KEY = settingsQueryKey();

/**
 * Admin Settings page (plan `playbooks-clay-wire-gaps`, Unit 3) — lets a
 * launcher admin view/edit/clear the Clay routine id `send.ts`'s
 * `resolvePlaybooksClayRoutineId` reads at send time, so this switch never
 * again requires an Infisical edit + a deploy.
 *
 * Gated on `isLauncherAdmin` at the PAGE level, not just the nav link
 * (`PlaybooksLayout.tsx` hides the link for a non-admin, but the route itself
 * is reachable by URL): `PATCH /settings` is the real, server-side gate
 * (`requireRole('launcher_admin', 'auth')` — AUTH-scoped, Key Decision 5, see
 * `api.ts`'s route registration), so a non-admin who lands here directly would
 * see a broken, always-failing form without this check.
 *
 * `useCurrentUser()`'s `isLoading` gates which "not admin" render this is: while
 * `/api/me` is still resolving, `roles` is `[]` by construction (its own
 * default before the query settles), which is indistinguishable from a real
 * non-admin — rendering the "you don't have access" Alert during that window
 * would flash a wrong denial at every admin on every load. A spinner instead,
 * same `query.isLoading` wrapper `PlaybooksSettingsForm` itself uses below.
 */
export function PlaybooksSettingsPage() {
  const { roles, isLoading } = useCurrentUser();
  const isAdmin = isLauncherAdmin(roles);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-16">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="px-8 py-8">
        <Alert status="warning">You don&apos;t have access to this page.</Alert>
      </div>
    );
  }

  return <PlaybooksSettingsForm />;
}

function PlaybooksSettingsForm() {
  const { apiFetch } = useApi();
  const queryClient = useQueryClient();

  // The pending confirm's target value (`null` clears the routine id, a
  // string sets it) — `null` here (the whole state, not the field) means
  // "dialog closed". A CONTROLLED dialog on purpose: see
  // `ConfirmActionDialog`'s doc comment for why `useAlertDialog()`'s
  // one-time content snapshot can't show a live `mutation.isPending`.
  const [pendingConfirm, setPendingConfirm] = useState<{ nextRoutineId: string | null } | null>(null);

  const query = useQuery({
    queryKey: SETTINGS_QUERY_KEY,
    queryFn: () => getPlaybooksSettings(apiFetch),
  });

  const saved = query.data?.clayRoutineId ?? null;
  const [draft, setDraft] = useState('');

  // Seed the local draft from the query result once it loads. This is a bare
  // `useState` + reseed effect, baselined under `terra/no-effect-reseeded-draft`:
  // a refetch while the user types would overwrite the draft, with or without
  // SSE. New code uses `useDraftState` (`docs/v4-system/draft-state-under-sse.md`).
  useEffect(() => {
    if (query.data) setDraft(query.data.clayRoutineId ?? '');
  }, [query.data]);

  const mutation = useMutation({
    mutationFn: (clayRoutineId: string | null) => updatePlaybooksSettings(apiFetch, { clayRoutineId }),
    onSuccess: (data) => {
      queryClient.setQueryData(SETTINGS_QUERY_KEY, data);
    },
    // No onError / manual toast here on purpose — the global
    // `MutationCache.onError` (`web/app-home/src/main.tsx`) is the app's one
    // error-toast path for a thrown `FetchErrorLike`; `apiFetch` already
    // throws a structured error on a non-2xx response (`_shared/hooks/useApi.ts`).
  });

  const normalizedDraft = normalizeRoutineIdInput(draft);
  const canSave = hasSettingsChange(saved, normalizedDraft);
  const canClear = saved !== null;

  function confirmAndSave(nextRoutineId: string | null) {
    setPendingConfirm({ nextRoutineId });
  }

  function handleConfirm() {
    // Guard against a concurrent mutation: `ConfirmActionDialog` already
    // disables Confirm while `pending`, but this is the belt-and-suspenders
    // check against the mutation's own in-flight state, in case a click
    // lands between "mutation started" and the next render disabling the
    // button.
    if (mutation.isPending || !pendingConfirm) return;
    mutation.mutate(pendingConfirm.nextRoutineId, {
      onSuccess: () => setPendingConfirm(null),
    });
  }

  if (query.isLoading) {
    return (
      <div className="flex items-center justify-center p-16">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (query.isError || !query.data) {
    return (
      <div className="px-8 py-8">
        <Alert status="error">Failed to load settings. Please try again.</Alert>
      </div>
    );
  }

  const lastChangedCaption =
    query.data.updatedAt && query.data.updatedByEmail
      ? `Last changed ${formatEventDate(query.data.updatedAt, {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        })} by ${query.data.updatedByEmail}`
      : null;

  return (
    <div className="flex-1 px-8 py-8">
      <div className="mb-2">
        <Heading size="xl">Settings</Heading>
      </div>

      <div className="mt-6 flex max-w-md flex-col gap-4">
        <Text size="sm" variant="tertiary">
          {describeTransport(saved)}
        </Text>

        <InputField
          label="Clay routine id"
          placeholder="e.g. workflow:wf_0tlf6uoWTASuf9eDC3g — not set uses the webhook"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
        />
        <Text size="xs" variant="tertiary">
          Must be prefixed — workflow:wf_… for a workflow, function:t_… for a function. A bare
          wf_…/t_… id is corrected automatically on save.
        </Text>

        {lastChangedCaption && (
          <Text size="xs" variant="tertiary">
            {lastChangedCaption}
          </Text>
        )}

        <div className="flex justify-end gap-2">
          <Button
            variant="secondary"
            disabled={!canClear || mutation.isPending}
            onClick={() => confirmAndSave(null)}
          >
            Clear
          </Button>
          <Button
            variant="primary"
            disabled={!canSave || mutation.isPending}
            onClick={() => confirmAndSave(normalizedDraft)}
          >
            Save
          </Button>
        </div>
      </div>

      <ConfirmActionDialog
        open={pendingConfirm !== null}
        onOpenChange={(next) => {
          if (!next) setPendingConfirm(null);
        }}
        title={
          pendingConfirm && pendingConfirm.nextRoutineId !== null
            ? 'Switch to a Clay routine run?'
            : 'Switch back to the webhook?'
        }
        description={
          pendingConfirm && pendingConfirm.nextRoutineId !== null ? (
            <>
              Future Playbooks sends, including Test Sends, will run as Clay routine runs of{' '}
              <strong>{pendingConfirm.nextRoutineId}</strong> instead of posting to the webhook.
              Sends already in flight are not affected. Run a Test Send afterward to verify.
            </>
          ) : (
            <>
              Future Playbooks sends, including Test Sends, will go back to posting to the webhook
              instead of running as a Clay routine run. Sends already in flight are not affected.
              Run a Test Send afterward to verify.
            </>
          )
        }
        confirmLabel="Confirm"
        pending={mutation.isPending}
        onConfirm={handleConfirm}
      />

      <LaunchedPlaysSection />
    </div>
  );
}

/**
 * "Launched plays" admin section (plan `playbooks-clay-wire-gaps`, U5b) —
 * mirrors Launcher's own "Launched campaigns" panel
 * (`mini-apps/launcher/components/LauncherSettingsPage.tsx`) for Playbooks'
 * plays: an admin escape hatch for a play stuck `'launched'` (a bad send, a
 * send that needs re-review) with no other UI path back to draft.
 *
 * Reads the SAME `usePlaysLibrary` every list surface shares (Home rows, the
 * Calendar popover, the Library section) — no new fetch, `launchedPlayRows`
 * just filters the one read this app already makes for a play list.
 *
 * Per-row pending state (`wipePendingId`/`resetPendingId`, NOT
 * `useMutation`'s own `isPending`) so one row's in-flight action never
 * disables the rest of the list — same reasoning `usePlayRowActions` already
 * uses for rename/delete: a single shared mutation hook's `isPending` is one
 * flag for the whole hook, not per-row, so two rows acting at once would
 * otherwise read as "the whole list is busy".
 */
function LaunchedPlaysSection() {
  const { apiFetch } = useApi();
  const queryClient = useQueryClient();
  const basePath = useAppBasePath();
  // `dirty: false` — this section owns no edits of its own; `useGuardedFlowEntry`
  // still asks before an "Open" mount would wipe a DIFFERENT flow's surviving
  // drafts (same seam Home's own "Open" goes through — `PlaybooksHome.tsx`'s
  // `handleOpen`).
  const openPlay = useGuardedFlowEntry({ dirty: false });

  const library = usePlaysLibrary();

  const playbookNameById = useMemo(
    () => mergePlaybookNames(undefined, library.data?.playbookNames),
    [library.data?.playbookNames],
  );
  const rows = useMemo(
    () => launchedPlayRows(library.data?.campaigns ?? []),
    [library.data?.campaigns],
  );

  const [wipeTarget, setWipeTarget] = useState<{
    playId: string;
    campaignSfdcId: string;
    displayName: string;
  } | null>(null);
  const [wipePendingId, setWipePendingId] = useState<string | null>(null);
  const [resetTarget, setResetTarget] = useState<{
    playId: string;
    campaignSfdcId: string;
    displayName: string;
  } | null>(null);
  const [resetPendingId, setResetPendingId] = useState<string | null>(null);

  async function handleConfirmWipe() {
    if (!wipeTarget) return;
    const { playId, campaignSfdcId, displayName } = wipeTarget;
    setWipePendingId(playId);
    try {
      const result = await wipePlayDispatchLog(apiFetch, playId);
      await Promise.all([
        invalidatePlayLists(queryClient, campaignSfdcId),
        queryClient.invalidateQueries({ queryKey: playSendsQueryKey(playId) }),
      ]);
      addToast({
        content: `Wiped ${result.deletedRows} dispatch log row(s) for "${displayName}".`,
        status: 'success',
      });
      setWipeTarget(null);
    } catch (err) {
      // Surfaces the server's own `problem()` message when there is one
      // (`rowActionErrorMessage`'s `userMessage` branch) — never
      // `MiniAppFetchError#message`, which falls back to a raw
      // `http_<status>:<status>` string when the server's body carried no
      // `detail`/`title`/`userMessage` (Codex review finding F3).
      addErrorToast({
        content: rowActionErrorMessage(err, `Couldn't wipe the log for "${displayName}". Try again.`),
      });
    } finally {
      setWipePendingId(null);
    }
  }

  async function handleConfirmReset() {
    if (!resetTarget) return;
    const { playId, campaignSfdcId, displayName } = resetTarget;
    setResetPendingId(playId);
    try {
      await resetPlayToDraft(apiFetch, playId);
      await invalidatePlayLists(queryClient, campaignSfdcId);
      addToast({ content: `"${displayName}" reset to draft.`, status: 'success' });
      setResetTarget(null);
    } catch (err) {
      // Same rule as the wipe-log handler above (Codex review finding F3) —
      // never `.message`, which can be a raw status code.
      addErrorToast({
        content: rowActionErrorMessage(err, `Couldn't reset "${displayName}" to draft. Try again.`),
      });
    } finally {
      setResetPendingId(null);
    }
  }

  return (
    <div className="mt-10 max-w-2xl">
      <div className="mb-1 flex items-center gap-2">
        <Heading size="lg">Launched plays</Heading>
        <Badge>{rows.length}</Badge>
      </div>
      <Text size="sm" variant="tertiary" className="mb-4 block">
        Wipe log clears a play&apos;s per-member delivery log. Reset to draft puts a play back into
        draft so it can be re-launched — its send history is kept either way.
      </Text>

      {library.isLoading ? (
        <div className="flex items-center justify-center p-8">
          <LoadingSpinner size="md" />
        </div>
      ) : library.isError ? (
        // Codex review finding F5: `library.data` is `undefined` on a failed
        // query, which used to fall through to `rows.length === 0` below and
        // silently render as "No launched plays yet." — indistinguishable
        // from the true empty state. This branch must come BEFORE that one.
        <Alert status="error">
          <AlertHeader>Couldn&apos;t load launched plays.</AlertHeader>
          <AlertDescription>
            <AlertInlineButton onClick={() => void library.refetch()}>Retry</AlertInlineButton>
          </AlertDescription>
        </Alert>
      ) : rows.length === 0 ? (
        <div className="rounded-lg border border-border-primary bg-bg-primary">
          <EmptySectionRow hasSearch={false} kind="launched plays" />
        </div>
      ) : (
        <div className="flex flex-col divide-y divide-border-secondary rounded-lg border border-border-primary bg-bg-primary">
          {rows.map(({ play, campaignName }) => {
            const displayName = playDisplayName(play, playbookNameById);
            const rowPending = wipePendingId === play.id || resetPendingId === play.id;
            return (
              <div key={play.id} className="flex items-center gap-3 px-4 py-3">
                <div className="min-w-0 flex-1">
                  <Text size="sm" weight="medium" className="block truncate">
                    {displayName}
                  </Text>
                  <Text size="xs" variant="tertiary" className="block truncate">
                    {campaignName ?? play.campaignSfdcId}
                    {' · '}
                    {formatEventDate(play.updatedAt) ?? ''}
                  </Text>
                </div>
                <StatusBadge status={playStatusBadgeStatus(play.status)}>
                  {playStatusLabel(play.status)}
                </StatusBadge>
                <span className="flex shrink-0 items-center gap-2">
                  <Button
                    variant="primary"
                    size="sm"
                    aria-label={`Open ${displayName}`}
                    disabled={rowPending}
                    onClick={() =>
                      openPlay(
                        `${basePath}/play/${play.id}?campaignSfdcId=${encodeURIComponent(play.campaignSfdcId)}`,
                      )
                    }
                  >
                    <Icon type={ArrowSquareOut} />
                    Open
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    aria-label={`Reset ${displayName} to draft`}
                    disabled={rowPending}
                    onClick={() =>
                      setResetTarget({ playId: play.id, campaignSfdcId: play.campaignSfdcId, displayName })
                    }
                  >
                    <Icon type={ArrowsClockwise} />
                    Reset to draft
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    aria-label={`Wipe dispatch log for ${displayName}`}
                    disabled={rowPending}
                    onClick={() =>
                      setWipeTarget({ playId: play.id, campaignSfdcId: play.campaignSfdcId, displayName })
                    }
                  >
                    <Icon type={TrashSimple} />
                    Wipe log
                  </Button>
                </span>
              </div>
            );
          })}
        </div>
      )}

      <ConfirmActionDialog
        open={wipeTarget !== null}
        onOpenChange={(next) => {
          if (!next) setWipeTarget(null);
        }}
        title={wipeTarget ? `Wipe the dispatch log for "${wipeTarget.displayName}"?` : ''}
        description={wipeTarget ? wipeLogWarning(wipeTarget.displayName) : ''}
        confirmLabel="Wipe log"
        variant="danger"
        pending={wipeTarget !== null && wipePendingId === wipeTarget.playId}
        onConfirm={() => void handleConfirmWipe()}
      />

      <ConfirmActionDialog
        open={resetTarget !== null}
        onOpenChange={(next) => {
          if (!next) setResetTarget(null);
        }}
        title={resetTarget ? `Reset "${resetTarget.displayName}" to draft?` : ''}
        description={resetTarget ? resetPlayWarning(resetTarget.displayName) : ''}
        confirmLabel="Reset to draft"
        variant="primary"
        pending={resetTarget !== null && resetPendingId === resetTarget.playId}
        onConfirm={() => void handleConfirmReset()}
      />
    </div>
  );
}
